import React from 'react';
import { Box, Divider, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { CollaborativeChartsReview, CollaborativeChartsReviewItemClinicalFlag, CollaborativeChartsReviewItem, collaborativeChartsReviewItemClinicalFlagReasonLabels } from '@uplift/api';
import { useCollaboratePhysicianMatchQuery, useCollaborativeChartsReviewClinicalFlagsQuery, useUserDisplayNameQuery } from '@uplift/queries';
import { LoadableTypography, ScrollableView } from '@uplift/ui-v2';
import { ChartsReviewLayout } from './ChartsReviewLayout';
import { SummaryScore } from './SummaryScore';
import { ReviewBlockTitle } from './ReviewBlock';
import { ReviewSummaryFeedbackForm } from './ReviewSummaryFeedbackForm';


const FlaggedResource: React.VFC<{
  chartsReviewItemFlag: CollaborativeChartsReviewItemClinicalFlag;
  chartsReviewItem: CollaborativeChartsReviewItem;
}> = ({ chartsReviewItemFlag, chartsReviewItem }) => {
  return (
    <Paper sx={{ px: 2, py: 1, pt: 2 }} variant="outlined">
      <React.Suspense fallback={<LoadableTypography width="250px" />}>
        <Typography variant="body1" fontWeight={500}>
          <ReviewBlockTitle resourceId={chartsReviewItem.resourceId} resourceType={chartsReviewItem.resourceType} />
        </Typography>
      </React.Suspense>
      <ListItemText primary={collaborativeChartsReviewItemClinicalFlagReasonLabels[chartsReviewItemFlag.reason]} secondary={chartsReviewItemFlag.details} />
    </Paper>
  );
};


export const ReviewSummaryBlock: React.VFC<{ chartsReview: CollaborativeChartsReview; chartsReviewItems: CollaborativeChartsReviewItem[] }> = ({ chartsReview, chartsReviewItems }) => {
  const { data: collaborativeMatch } = useCollaboratePhysicianMatchQuery(chartsReview.matchId);
  const { data: clinicalReviewClinicalFlagByResource } = useCollaborativeChartsReviewClinicalFlagsQuery(chartsReview.id);
  const { data: providerName } = useUserDisplayNameQuery(collaborativeMatch.providerId, { suspense: true });
  const freq = { weekly: 'week', monthly: 'month', quarterly: 'quarter', annually: 'year' }[chartsReview.reviewFrequency];
  const scoreSummary = chartsReviewItems.length ? Math.round(chartsReviewItems.reduce((prev, curr) => prev += curr.score, 0) / chartsReviewItems.length) : 0;
  const clinicalReviewClinicalFlags = Object.values(clinicalReviewClinicalFlagByResource);

  return (
    <ChartsReviewLayout
      title="Review Summary"
      left={(
        <Stack gap={3} flex={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h3">Overall Score</Typography>
              <Typography variant="body2">Result based on questionnaires</Typography>
            </Box>
            <Box>
              <SummaryScore score={scoreSummary} />
            </Box>
          </Stack>

          <Divider />

          <Box>
            <Typography variant="overline" color="secondary">Summary</Typography>
            <Typography component="ul" sx={{ pl: 3 }}>
              <li>You reviewed {chartsReviewItems.length} of {providerName.first}’s charts for the last {freq}</li>
            </Typography>
          </Box>

          {clinicalReviewClinicalFlags?.length ? (
            <>
              <Divider />

              <Box>
                <Typography variant="overline" color="secondary">Flagged for clinical review</Typography>
                <Stack sx={{ mt: 0.5 }} gap={1}>
                  {Object.values(clinicalReviewClinicalFlagByResource).map(flag => (
                    <FlaggedResource key={flag.resourceId} chartsReviewItemFlag={flag} chartsReviewItem={chartsReviewItems.find(i => i.resourceId === flag.resourceId)} />
                  ))}
                </Stack>
              </Box>
            </>
          ) : null}

        </Stack>
      )}
      right={(
        <ScrollableView sx={{ bgcolor: 'grey.50' }} showScrollShadow>
          <ReviewSummaryFeedbackForm chartsReview={chartsReview} />
        </ScrollableView>
      )}
    />
  );
};
